import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";

type SourceHeatmap = {
  source_id: number;
  code: string;
  name: string;
  probes: number[][];   // [7][24]
  changes: number[][];  // [7][24]
  total_probes: number;
  total_changes: number;
};
type HeatResp = { days: number; since: string; day_labels: string[]; sources: SourceHeatmap[] };

type RecentItem = {
  id: number; probed_at: string; source_code: string; source_name: string;
  status_code: number | null; last_modified: string | null; etag: string | null;
  content_length: number | null; changed: boolean; triggered_scrape: boolean;
};
type RecentResp = { items: RecentItem[] };

type SummaryRow = {
  source_code: string; source_name: string;
  probes: number; changes: number; triggered: number;
  last_modified_seen: string | null; last_probe_at: string | null;
};
type SummaryResp = { days: number; by_source: SummaryRow[] };

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

function cellColor(v: number, maxV: number, type: "probes" | "changes", primaryColor: string): string {
  if (v === 0) return "transparent";
  const t = Math.min(1, v / Math.max(1, maxV));
  if (type === "changes") {
    const alpha = 0.25 + 0.65 * t;
    return `rgba(245, 158, 11, ${alpha})`;
  }
  const alpha = 0.10 + 0.55 * t;
  return `rgba(${hexToRgb(primaryColor)}, ${alpha})`;
}

function Heatmap({ src, dayLabels, mode, primaryColor }: { src: SourceHeatmap; dayLabels: string[]; mode: "probes" | "changes"; primaryColor: string }) {
  const matrix = mode === "probes" ? src.probes : src.changes;
  const maxV = Math.max(...matrix.flat(), 1);
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: "0.65rem", minWidth: 560 }}>
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            {HOURS.map(h => (
              <th key={h} style={{ width: 22, padding: "2px 0", color: "var(--clr-text4)", fontWeight: 500 }}>
                {h % 3 === 0 ? h : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dayLabels.map((d, dow) => (
            <tr key={dow}>
              <td style={{ padding: "0 4px", color: "var(--clr-text3)", fontWeight: 600, textAlign: "right" }}>{d}</td>
              {HOURS.map(h => {
                const v = matrix[dow][h];
                return (
                  <td key={h} title={`${d} ${h}:00 — ${v} ${mode}`} style={{
                    width: 22, height: 22, padding: 0,
                    background: cellColor(v, maxV, mode, primaryColor),
                    border: "1px solid var(--clr-border2, rgba(0,0,0,0.05))",
                    color: v > maxV * 0.6 ? "white" : "var(--clr-text2)",
                    textAlign: "center", fontSize: "0.6rem", fontWeight: 600,
                  }}>{v > 0 ? v : ""}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ProbesPage() {
  const G = useAuthStore(s => s.user?.primary_color) || "#0891b2";
  const [days, setDays] = useState(30);
  const [mode, setMode] = useState<"probes" | "changes">("probes");
  const [heat, setHeat] = useState<HeatResp | null>(null);
  const [recent, setRecent] = useState<RecentItem[]>([]);
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    setLoading(true); setErr("");
    Promise.all([
      api.get<HeatResp>(`/probes/heatmap?days=${days}`),
      api.get<RecentResp>(`/probes/recent?limit=80${onlyChanged ? "&only_changed=true" : ""}`),
      api.get<SummaryResp>(`/probes/summary?days=${days}`),
    ])
      .then(([h, r, s]) => { setHeat(h); setRecent(r.items); setSummary(s.by_source); })
      .catch(e => setErr(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [days, onlyChanged, retryKey]);

  return (
    <div className="page" style={{ color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>Patrones de actualización</h1>
          <p style={{ margin: "4px 0 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
            Analiza cuándo cambian las webs ministeriales para optimizar las ventanas de scraping.
            Hora Madrid (Europe/Madrid).
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {([["probes", "Probes"], ["changes", "Cambios"]] as const).map(([k, label]) => (
            <button key={k} onClick={() => setMode(k)} style={{
              background: mode === k ? "var(--clr-org-primary)" : "var(--clr-surface)",
              color: mode === k ? "#fff" : "var(--clr-text3)",
              border: mode === k ? "1px solid transparent" : "1px solid var(--clr-border)",
              borderRadius: 999, padding: "0.42rem 1rem", fontSize: "0.78rem",
              fontWeight: mode === k ? 600 : 500, cursor: "pointer", transition: "all 0.15s",
            }}>{label}</button>
          ))}
          <div style={{ width: 1, height: 20, background: "var(--clr-border2)", margin: "0 2px" }} />
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              background: days === d ? "var(--clr-org-primary)" : "var(--clr-surface)",
              color: days === d ? "#fff" : "var(--clr-text3)",
              border: days === d ? "1px solid transparent" : "1px solid var(--clr-border)",
              borderRadius: 999, padding: "0.42rem 1rem", fontSize: "0.78rem",
              fontWeight: days === d ? 600 : 500, cursor: "pointer", transition: "all 0.15s",
            }}>{d}d</button>
          ))}
        </div>
      </div>

      {loading && <div style={{ padding: 40, textAlign: "center", color: "var(--clr-text4)" }}>Cargando…</div>}
      {err && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "12px 16px", marginBottom: 16, flexWrap: "wrap", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: "#ef4444", flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: "0.85rem", color: "var(--clr-text2)", minWidth: 180 }}>No se pudo cargar la información. Comprueba tu conexión.</span>
          <button onClick={() => setRetryKey(k => k + 1)} style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "8px 18px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>Reintentar</button>
        </div>
      )}

      {!loading && !err && summary.length === 0 && (
        <div style={{ padding: 60, textAlign: "center", background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14, color: "var(--clr-text4)" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12a7 7 0 0 1 7-7" /><path d="M5 16a11 11 0 0 1 11-11" /><circle cx="6" cy="18" r="1.5" /></svg>
          </div>
          <div style={{ fontSize: "0.95rem", fontWeight: 700, marginBottom: 8, letterSpacing: "-0.01em" }}>Aún no hay probes registrados</div>
          <div style={{ fontSize: "0.82rem", color: "var(--clr-text4)", maxWidth: 460, margin: "0 auto", lineHeight: 1.5 }}>
            El sistema empieza a probar cada fuente cada 30 min en horario laboral (lun-vie 9–17 Madrid).
            Tras 1-2 días verás los primeros patrones aquí.
          </div>
        </div>
      )}

      {/* Tabla resumen */}
      {summary.length > 0 && (
        <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, marginBottom: 20, padding: "1rem 1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: "0.95rem", fontWeight: 700, letterSpacing: "-0.01em" }}>Resumen últimos {days} días</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--clr-border2)" }}>
                  {[["Fuente", "left"], ["Probes", "right"], ["Cambios", "right"], ["% cambio", "right"], ["Scrapes disparados", "right"], ["Last-Modified visto", "left"]].map(([h, align]) => (
                    <th key={h} style={{ padding: "0.7rem 12px", textAlign: align as "left" | "right", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                    <td style={{ padding: "0.7rem 12px", fontWeight: 600, color: "var(--clr-text)" }}>{r.source_name}</td>
                    <td style={{ padding: "0.7rem 12px", textAlign: "right", color: "var(--clr-text2)" }}>{r.probes.toLocaleString("es-ES")}</td>
                    <td style={{ padding: "0.7rem 12px", textAlign: "right", fontWeight: 600, color: "var(--clr-text)" }}>{r.changes.toLocaleString("es-ES")}</td>
                    <td style={{ padding: "0.7rem 12px", textAlign: "right", color: "var(--clr-text3)" }}>
                      {r.probes > 0 ? `${(100 * r.changes / r.probes).toFixed(1)}%` : "—"}
                    </td>
                    <td style={{ padding: "0.7rem 12px", textAlign: "right", color: "var(--clr-text2)" }}>{r.triggered.toLocaleString("es-ES")}</td>
                    <td style={{ padding: "0.7rem 12px", color: "var(--clr-text3)", fontSize: "0.75rem" }}>{r.last_modified_seen || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Heatmap por fuente */}
      {heat && heat.sources.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(580px, 100%), 1fr))", gap: 16, marginBottom: 20 }}>
          {heat.sources.map(s => (
            <div key={s.source_id} style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1rem 1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h4 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{s.name}</h4>
                <span style={{ fontSize: "0.7rem", color: "var(--clr-text4)" }}>
                  {mode === "probes"
                    ? <><span style={{ color: "var(--clr-text2)", fontWeight: 600 }}>{s.total_probes.toLocaleString("es-ES")}</span> probes</>
                    : <><span style={{ color: "var(--clr-text2)", fontWeight: 600 }}>{s.total_changes.toLocaleString("es-ES")}</span> cambios</>}
                </span>
              </div>
              <Heatmap src={s} dayLabels={heat.day_labels} mode={mode} primaryColor={G} />
            </div>
          ))}
        </div>
      )}

      {/* Probes recientes */}
      {recent.length > 0 && (
        <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1rem 1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, letterSpacing: "-0.01em" }}>Probes recientes</h3>
            <label style={{ fontSize: "0.78rem", color: "var(--clr-text3)", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={onlyChanged} onChange={e => setOnlyChanged(e.target.checked)} style={{ accentColor: "var(--clr-org-primary)" }} />
              Solo con cambio detectado
            </label>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--clr-border2)" }}>
                  {[["Fecha/hora", "left"], ["Fuente", "left"], ["HTTP", "right"], ["Last-Modified", "left"], ["Tamaño", "right"], ["Cambio", "left"]].map(([h, align]) => (
                    <th key={h} style={{ padding: "0.6rem 10px", textAlign: align as "left" | "right", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent.map(r => (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                    <td style={{ padding: "0.6rem 10px", color: "var(--clr-text3)", whiteSpace: "nowrap" }}>
                      {new Date(r.probed_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td style={{ padding: "0.6rem 10px", fontWeight: 500, color: "var(--clr-text2)" }}>{r.source_name}</td>
                    <td style={{ padding: "0.6rem 10px", textAlign: "right" }}>
                      {/* Estado HTTP en pastilla tintada (patrón StatusPill) */}
                      {(() => {
                        const c = r.status_code === 200 ? "#16a34a" : r.status_code == null ? "#94a3b8" : "#ef4444";
                        return (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            background: c + "1e", color: c,
                            borderRadius: 999, padding: "2px 9px",
                            fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap",
                          }}>
                            {r.status_code ?? "—"}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "0.6rem 10px", color: "var(--clr-text3)", fontSize: "0.72rem" }}>{r.last_modified || "—"}</td>
                    <td style={{ padding: "0.6rem 10px", textAlign: "right", color: "var(--clr-text3)" }}>
                      {r.content_length ? `${(r.content_length / 1024).toFixed(1)} KB` : "—"}
                    </td>
                    <td style={{ padding: "0.6rem 10px" }}>
                      {r.changed
                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.72rem", color: "var(--clr-text3)", whiteSpace: "nowrap" }}>
                            <span style={{ width: 6, height: 6, borderRadius: 3, background: "#f59e0b", flexShrink: 0 }} />
                            Cambio{r.triggered_scrape && " · scrape disparado"}
                          </span>
                        : <span style={{ color: "var(--clr-text4)" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
